import React, { useCallback, useState, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { v4 as uuid } from 'uuid'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Table from 'components/table/SimpleTable'

import Window from 'components/dialog/Window'
import FormWrapper from 'components/form/Wrapper'
import FormRender from 'components/form/Render'
import FormDialog from 'components/form/Dialog'

import dateUtils from 'shared/src/date'
import snackbarActions from 'store/modules/snackbar'
import bookSelectors from 'store/selectors/booking'

const DAY_SCHEMA = [{
  id: 'name',
  title: 'Day Header',
  helperText: 'Enter the header to appear at the top of the day - can be left blank',
}, {
  id: 'description',
  title: 'Description',
  helperText: 'Enter the description of the day - can be left blank',
  component: 'textarea',
  rows: 3,
}]

const TABLE_FIELDS = [{
  title: 'Name',
  name: 'name',
}, {
  title: 'Details',
  name: 'details',
}]

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
  },
  title: {
    flexGrow: 1,
  },
  addButton: {
    flexGrow: 0,
  },
  actionButton: {
    margin: theme.spacing(1),
  },
  slotDetails: {
    fontSize: '0.8em',
    color: '#999'
  },
}))


const EditDayDialog = ({
  day,
  error,
  onSubmit,
  onCancel,
  onDeleteDay,
}) => {

  const dispatch = useDispatch()
  const classes = useStyles()

  const [ editingSlot, setEditingSlot ] = useState(null)
  const [ deletingSlot, setDeletingSlot ] = useState(null)
  const [ deletingDay, setDeletingDay ] = useState(false)
  const [ slots, setSlots ] = useState([])

  const dateTitle = useMemo(() => {
    const date = dateUtils.stringToDate(day.date)
    return dateUtils.title(date)
  }, [
    day.date,
  ])

  const slotOverrideFormSchema = useSelector(bookSelectors.daySlotOverrideFormSchema)

  const onSubmitHandler = useCallback((formValues) => {
    onSubmit(Object.assign({}, formValues, {
      slots,
    }))
  }, [
    slots,
  ])

  const onSaveSlot = useCallback((formValues) => {
    const isNew = editingSlot.id ? false : true
    const id = editingSlot.id ? editingSlot.id : uuid()
    const name = formValues.name
    const data = Object.assign({}, formValues)
    delete(data.name)
    const slot = {
      id,
      name,
      data,
    }
    const newSlots = isNew ?
      slots.concat([slot]) :
      slots.map(s => {
        return s.id == id ? slot : s
      })
    setSlots(newSlots)
    setEditingSlot(null)
    dispatch(snackbarActions.setSuccess('slots updated'))
  }, [
    editingSlot,
    slots,
  ])

  const onEditSlot = useCallback((slot) => {
    setEditingSlot({
      id: slot.id,
      name: slot.name,
      ...slot.data,
    })
  }, [])

  const onConfirmDeleteSlot = useCallback((slot) => {
    const newSlots = slots.filter(s => s.id != deletingSlot.id)
    setSlots(newSlots)
    setDeletingSlot(null)
    dispatch(snackbarActions.setSuccess('slot deleted'))
  }, [
    slots,
    deletingSlot,
  ])

  const moveSlot = useCallback((id, direction) => {
    const currentIndex = slots.findIndex(s => s.id == id)
    const currentSlot = slots.find(s => s.id == id)
    const newIndex = currentIndex + direction
    const newSlots = [].concat(slots)
    const target = newSlots[newIndex]
    newSlots[currentIndex] = target
    newSlots[newIndex] = currentSlot
    setSlots(newSlots)
    dispatch(snackbarActions.setSuccess('slot moved'))
  }, [
    slots,
  ])

  const tableData = useMemo(() => {
    return slots.map((slot, index) => {
      return {
        index,
        id: slot.id,
        name: slot.name,
        data: slot,
        details: (
          <ul className={ classes.slotDetails }>
            {
              Object.keys(slot.data).map((field, i) => {
                return (
                  <li key={ i }>
                    { field }={ slot.data[field] }
                  </li>
                )
              })
            }
          </ul>
        )
      }
    })
  }, [
    slots,
  ])

  const getActions = useCallback((item) => {
    return (
      <>
        <Button
          className={ classes.actionButton }
          size="small"
          variant="outlined"
          disabled={ item.index <= 0 }
          onClick={ () => moveSlot(item.id, -1) }
        >
          Up
        </Button>
        <Button
          className={ classes.actionButton }
          size="small"
          variant="outlined"
          disabled={ item.index >= slots.length - 1 }
          onClick={ () => moveSlot(item.id, 1) }
        >
          Down
        </Button>
        <Button
          className={ classes.actionButton }
          size="small"
          variant="outlined"
          onClick={ () => setDeletingSlot(item.data) }
        >
          Delete
        </Button>
        <Button
          className={ classes.actionButton }
          size="small"
          variant="outlined"
          onClick={ () => onEditSlot(item.data) }
        >
          Edit
        </Button>
      </>
    )
  }, [
    slots,
  ])

  useEffect(() => {
    if(!day) return
    if(!day.meta.overridenDay) return
    setSlots(day.meta.overridenDay.data.slots || [])
  }, [
    day,
  ])

  return (
    <>
      <FormWrapper
        schema={ DAY_SCHEMA }
        initialValues={ {
          name: day.name,
          description: day.meta.description,
        }}
        onSubmit={ onSubmitHandler }
      >
        {
          ({
            isValid,
            values,
            errors,
            showErrors,
            touched,
            onSubmit,
          }) => {
            return (
              <Window
                open
                size="lg"
                withCancel
                fullHeight
                onSubmit={ onSubmit }
                onCancel={ onCancel }
              >
                <Grid container spacing={ 2 }>
                  <Grid item xs={ 12 } sm={ 5 }>
                    <div className={ classes.toolbar }>
                      <div className={ classes.title }>
                        <Typography gutterBottom variant="h6">
                          { dateTitle }
                        </Typography>
                      </div>
                    </div>
                    <div className={ classes.content }>
                      <FormRender
                        fullHeight
                        schema={ DAY_SCHEMA }
                        error={ error }
                        isValid={ isValid }
                        values={ values }
                        errors={ errors }
                        touched={ touched }
                        onSubmit={ onSubmit }
                      />
                    </div>
                  </Grid>
                  <Grid item xs={ 12 } sm={ 7 }>
                    <div className={ classes.toolbar }>
                      <div className={ classes.title }>
                        <Typography gutterBottom variant="h6">
                          Slots
                        </Typography>
                      </div>
                      <div className={ classes.addButton }>
                        <Button
                          className={ classes.actionButton }
                          variant="outlined"
                          size="small"
                          onClick={ () => setDeletingDay(day) }
                        >
                          Reset Day
                        </Button>
                        <Button
                          className={ classes.actionButton }
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={ () => setEditingSlot({}) }
                        >
                          Add Slot
                        </Button>
                      </div>
                    </div>
                    <div className={ classes.content }>
                      <Table
                        data={ tableData }
                        fields={ TABLE_FIELDS }
                        getActions={ getActions }
                      />
                    </div>
                  </Grid>
                </Grid>
              </Window>
            )
          }
        }
      </FormWrapper>
      {
        editingSlot && (
          <FormDialog
            withCancel
            size="sm"
            schema={ slotOverrideFormSchema }
            initialValues={ editingSlot }
            onCancel={ () => setEditingSlot(null) }
            onSubmit={ onSaveSlot }
          />
        )
      }
      {
        deletingSlot && (
          <Window
            open
            size="sm"
            title="Delete slot?"
            withCancel
            submitTitle="Delete"
            onSubmit={ onConfirmDeleteSlot }
            onCancel={ () => setDeletingSlot(null) }
          >
            Are you sure you want to delete this slot?
          </Window>
        )
      }
      {
        deletingDay && (
          <Window
            open
            size="sm"
            title="Reset day?"
            withCancel
            submitTitle="Reset"
            onSubmit={ () => {
              setDeletingDay(false)
              onDeleteDay() 
            }}
            onCancel={ () => setDeletingDay(false) }
          >
            <Typography gutterBottom>
              Are you sure you want to reset this day?
            </Typography>
            <Typography gutterBottom>
              <strong>WARNING: </strong> if there are bookings on this day this will cause problems.
            </Typography>
          </Window>
        )
      }
    </>
  )
}

export default EditDayDialog